import { useEffect, useRef } from "react";
import { CheckValue } from "../../../../../../Service/service";

/**
 * Smoothly centers a canvas on a target item when a `targetId` is provided.
 * Prevents re-centering on scale changes unless `targetId` changes.
 *
 * @param {object} options
 * @param {string | null} options.targetId - ID of the drone or item to center on.
 * @param {Array} options.items - List of objects with `{ id, latitude, longitude }`.
 * @param {number} options.scale - Current canvas scale.
 * @param {object} options.canvasRef - Ref to the canvas element.
 * @param {object} options.containerRef - Ref to the canvas container.
 * @param {function} options.setPosition - Updates canvas offset position.
 * @param {boolean} options.isCenter - Whether to center the full canvas.
 * @param {function} options.setAction - Action setter to toggle isCenter.
 * @param {HTMLImageElement} options.image - Loaded floor plan image.
 */
const useSmoothCenterOnItem = ({
  targetId,
  items,
  scale,
  canvasRef,
  containerRef,
  setPosition,
  isCenter,
  setAction,
  image,
}) => {
  const hasCenteredRef = useRef(null); // Prevents redundant centering

  /**
   * Smoothly animates the canvas position from current to target
   */
  const animatePosition = ({ x: targetX, y: targetY }, duration = 300) => {
    const startX = canvasRef.current?.style.left
      ? parseFloat(canvasRef.current.style.left)
      : 0;
    const startY = canvasRef.current?.style.top
      ? parseFloat(canvasRef.current.style.top)
      : 0;

    const startTime = performance.now();

    const animate = (time) => {
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const ease = 0.5 - Math.cos(progress * Math.PI) / 2; // easeInOut

      const newX = startX + (targetX - startX) * ease;
      const newY = startY + (targetY - startY) * ease;
      setPosition({ x: newX, y: newY });

      if (progress < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  };

  useEffect(() => {
    if (!canvasRef.current || !containerRef.current || !image || scale <= 0)
      return;

    const containerRect = containerRef.current.getBoundingClientRect();

    // Case 1: Center on a specific item by ID
    if (CheckValue(targetId)) {
      if (!items.length || hasCenteredRef.current === targetId) return;
      const targetItem = items.find((item) => item.id === targetId);
      if (!targetItem) return;

      const { longitude: x, latitude: y } = targetItem;
      const scaledX = x * scale;
      const scaledY = y * scale;

      const centerX = containerRect.width / 2;
      const centerY = containerRef.current.getBoundingClientRect().height / 2;

      const newX = centerX - scaledX;
      const newY = centerY - scaledY;

      animatePosition({ x: newX, y: newY });
      hasCenteredRef.current = targetId;
      return;
    }
    // Case 2: Center the whole canvas on first image load
    if (!isCenter) return;

    const canvas = canvasRef.current;
    canvas.width = image.width * scale;
    canvas.height = image.height * scale;

    const centerX = (containerRect.width - canvas.width) / 2;
    const centerY = (containerRect.height - canvas.height) / 2;

    animatePosition({ x: centerX, y: centerY });
    setAction((prev) => ({ ...prev, isCenter: false }));
    return;
  }, [
    targetId,
    items,
    scale,
    image,
    isCenter,
    canvasRef,
    containerRef,
    setPosition,
    setAction,
  ]);

  // Reset tracking when ID is cleared
  useEffect(() => {
    if (!targetId) {
      hasCenteredRef.current = null;
    }
  }, [targetId]);
};

export default useSmoothCenterOnItem;
